import { resolveAgentChannels } from "../../models/agent.model";
import {
  getConversationDisplayStatus,
  isNeedsAssign,
} from "../../utils/ticketStatus";

export type HandledByAiFilter = "all" | "high" | "medium" | "low";
export type HandledByAiStatusFilter = "all" | "ai" | "not_ai";

export const pickReportRecipientCount = (...values: Array<number | null | undefined>) => {
  for (const value of values) {
    const n = Number(value);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return 0;
};

export const formatCount = (value?: number | null): string => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return Math.round(n).toLocaleString("en-US");
};

export const toPct = (value: unknown): number => {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return 0;
  if (n <= 1) return Math.round(n * 100);
  return Math.min(100, Math.round(n));
};

export const matchesHandledByAiFilter = (
  rate: number,
  filter: HandledByAiFilter
): boolean => {
  if (filter === "high") return rate >= 0.8;
  if (filter === "medium") return rate >= 0.5 && rate < 0.8;
  if (filter === "low") return rate < 0.5;
  return true;
};

export const getRowChannels = (row: any): string[] => {
  if (Array.isArray(row?.channels) && row.channels.length) {
    return resolveAgentChannels(row.channels);
  }
  return [normalizeChannel(row?.channel)];
};

export const rowHasEnabledChannel = (
  row: any,
  isEnabled: (key: string) => boolean
): boolean => getRowChannels(row).some((channel) => isEnabled(channel));

export const normalizeChannel = (value?: string) => {
  const raw = String(value || "call").toLowerCase();
  if (raw.includes("whatsapp") || raw === "forum") return "whatsapp";
  if (raw.includes("mail") || raw.includes("email")) return "email";
  if (raw.includes("sms") || raw.includes("text")) return "sms";
  return "call";
};

export const formatReportChannel = (row: any, fallback?: string): string => {
  const fromRow = [
    ...(Array.isArray(row?.channels_used) ? row.channels_used : []),
    row?.channel,
  ]
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .map((value) => normalizeChannel(value));
  const unique = fromRow.filter((token, index) => fromRow.indexOf(token) === index);
  if (unique.length) return unique.join(" · ");
  const fallbackText = String(fallback || "").trim();
  return fallbackText ? normalizeChannel(fallbackText) : "—";
};

export const inboxApiChannel = (channelKey: string): string | null => {
  const key = String(channelKey || "").toLowerCase();
  if (key === "sms") return "SMS";
  if (key === "email") return "EMAIL";
  if (key === "whatsapp") return "WHATSAPP_MESSAGE";
  return null;
};

export const inboundReportInboxChannel = (channels: string[]): string => {
  const api = (Array.isArray(channels) ? channels : [])
    .map((channel) => inboxApiChannel(channel))
    .filter((channel): channel is string => Boolean(channel));
  const unique = api.filter((channel, index) => api.indexOf(channel) === index);
  if (unique.length > 1) return "ALL";
  return unique[0] || "WHATSAPP_MESSAGE";
};

export const uniqueReportRows = (rows: any[]) => {
  const seen = new Set<string>();
  return (Array.isArray(rows) ? rows : []).filter((item) => {
    const key = String(item?._id || item?.id || item?.key || "");
    if (!key) return true;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const conversationBelongsToInboundAgent = (
  item: any,
  agentId?: string
) => {
  if (!agentId) return true;
  const ids = [
    item?.agent_id?._id || item?.agent_id,
    item?.last_agent_id?._id || item?.last_agent_id,
    item?.primary_agent_id?._id || item?.primary_agent_id,
  ]
    .filter(Boolean)
    .map((value) => String(value));
  if (!ids.length) return true;
  return ids.includes(String(agentId));
};

export const getRecipientCount = (row: any): number => {
  const stats = row?.stats || {};
  const n = Number(
    stats.number_of_recipients ??
      row?.number_of_recipients ??
      stats.number_of_calls ??
      0
  );
  return Number.isFinite(n) ? n : 0;
};

export const getHandledByAiRate = (row: any): number => {
  const stats = row?.stats || {};
  const raw = stats.handled_by_ai_rate ?? stats.completion_rate ?? 0;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return 0;
  return n > 1 ? Math.min(1, n / 100) : n;
};

export const getReplyRate = (row: any): number | null => {
  const stats = row?.stats || {};
  if (stats.reply_rate === undefined || stats.reply_rate === null) {
    if (stats.answered_calls != null && stats.number_of_calls) {
      return Number(stats.answered_calls) / Math.max(Number(stats.number_of_calls), 1);
    }
    return null;
  }
  const n = Number(stats.reply_rate);
  if (!Number.isFinite(n) || n < 0) return null;
  return n > 1 ? Math.min(1, n / 100) : n;
};

export const isCallAnswered = (row: any): boolean => {
  const status = String(row?.status || "").toLowerCase();
  const duration = Number(row?.call_duration ?? row?.duration ?? 0);
  if (duration > 0) return true;
  return status === "completed" || status === "answered";
};

export const isGoalAchieved = (row: any): boolean =>
  String(row?.call_analysis?.goal_achieved || "").toLowerCase() === "achieved";

export const isCallHandledByAi = (row: any): boolean => {
  const status = String(row?.status || "").toLowerCase();
  if (status.includes("transfer")) return false;
  if (
    status === "no-answer" ||
    status === "failed" ||
    status === "busy" ||
    status === "canceled" ||
    status === "cancelled" ||
    status === "rejected"
  ) {
    return false;
  }
  return isCallAnswered(row);
};

/** Same buckets as Inbox: open, escalated (=need_assign), failed, completed. */
export { getConversationDisplayStatus };

export const isConversationHandledByAi = (row: any): boolean =>
  getConversationDisplayStatus(row) === "completed";

export const isConversationEscalated = (row: any): boolean =>
  getConversationDisplayStatus(row) === "escalated" || isNeedsAssign(row);

export const getCallDurationSeconds = (row: any): number => {
  const n = Number(row?.call_duration ?? row?.duration ?? 0);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

export const getCallSummaryText = (row: any): string => {
  const analysis = row?.call_analysis || {};
  return (
    analysis.summary ||
    analysis.root_cause_analysis?.summary ||
    row?.summary ||
    ""
  );
};

export const isPrimaryCallChannel = (row: any): boolean => {
  const channels = getRowChannels(row);
  return channels[0] === "call";
};
